import { useEffect } from 'react';

import { DYNAMIC_LEGACY_SCREENER_KEYS } from '@/modules/api';
import { useRulesEngine } from '@/modules/rulesEngine';
import { LoadingScreen } from '@/modules/ui';

import { LegacyAssignedScreenerFlow } from './LegacyAssignedScreenerFlow';

/**
 * The onboarding legacy assigned-screeners step (`/step/assigned-screeners`) —
 * the backend-assigned screeners flow, served in BOTH experiment arms:
 *   - control arm → serves every backend-assigned screener (no exclusion). The
 *     resolver walks control here via the linear survey order (health-conditions
 *     → health-goals → assigned-screeners).
 *   - test arm → runs AFTER the dynamic-assessment pass (`/step/dynamic-assessment`)
 *     as the safety net for any screener dynamic assessment doesn't support yet,
 *     excluding the migrated keys so they aren't re-asked. Today every assigned
 *     screener is migrated, so this typically fetches an empty list and advances.
 *
 * The `onb_use_dynamic_assessment` flag is read only to pick `excludeKeys` (the
 * dynamic pass itself lives on `DynamicAssessmentScreen` now). Until the flag
 * resolves, hold a spinner so the flow mounts with the correct exclusion (its
 * data hook fires on mount). An `'error'` read fails open to control (no
 * exclusion), as does a latched `dynamicAssessmentErrored`: if the dynamic pass
 * failed, its screeners were never served, so this legacy pass must serve them
 * all rather than exclude the "migrated" keys and drop them entirely.
 */
export function AssignedScreenersScreen() {
  const { dynamicAssessmentEnabled, ensureDynamicAssessment, dynamicAssessmentErrored } =
    useRulesEngine();

  // Deduped; the driver usually read the flag already to pick the survey start.
  useEffect(() => {
    ensureDynamicAssessment();
  }, [ensureDynamicAssessment]);

  // Hold the spinner until the flag resolves so the flow mounts once with the
  // right `excludeKeys` (control → none; test → the migrated keys). An `'error'`
  // read counts as resolved and fails open to control.
  const resolved =
    dynamicAssessmentEnabled.status === 'ready' || dynamicAssessmentEnabled.status === 'error';
  if (!resolved) {
    return <LoadingScreen />;
  }

  const isTestArm =
    dynamicAssessmentEnabled.status === 'ready' && dynamicAssessmentEnabled.value === true;

  // Test arm excludes the migrated keys (already answered + written via the
  // dynamic terminal effects); control serves every screener. If the dynamic
  // pass errored, exclude nothing — those screeners weren't served, so a
  // stale/back-nav landing here must still ask them (mirrors the pre-split
  // `dynamicErrored ? undefined : keys` contract).
  return (
    <LegacyAssignedScreenerFlow
      excludeKeys={
        isTestArm && !dynamicAssessmentErrored ? DYNAMIC_LEGACY_SCREENER_KEYS : undefined
      }
    />
  );
}
